import { useAnalytics } from "@codat/analytics";
import { Modal, SelectInput, useToast } from "@codat/orchard-ui";
import { ApiError, CodatUser, UserRole } from "api/users";
import { AxiosError } from "axios";
import React from "react";
import { Controller, useForm } from "react-hook-form";

import TextInput from "components/TextInput";

import { useUserManagementService } from "../UserManagementService";

export interface EditUserModalProps {
  open: boolean;
  user: CodatUser;
  userRoleOptions: UserRole[];
  onClose: () => void;
  onSuccess: (updatedUser: CodatUser) => void;
  isOnlyAdmin: boolean;
}

interface FormData {
  name: string;
  role: UserRole;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  user,
  userRoleOptions,
  onClose,
  onSuccess,
  isOnlyAdmin,
}) => {
  const {
    formState,
    register,
    getValues,
    handleSubmit,
    control,
    setError,
    clearErrors,
  } = useForm<FormData>({
    mode: "onBlur",
    defaultValues: {
      name: user.name,
      role: user.role,
    },
  });

  const service = useUserManagementService()?.editUser;

  if (!service) {
    throw new Error("No User Management Service provider");
  }

  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();

  const updateUser = (formData: FormData) => {
    sendEvent("Save Changes Button - Click");
    const valuesChanged =
      JSON.stringify(formState.defaultValues) !== JSON.stringify(getValues());
    if (!valuesChanged) {
      onClose();
      return;
    }

    service
      .editUser({ ...user, ...formData })
      .then((updatedUserResponse) => {
        addToast({
          message: `Updated user`,
          variant: "success",
        });
        onSuccess(updatedUserResponse.data);
      })
      .catch((error: AxiosError<ApiError>) => {
        const errorMessage = error.response?.data?.error ?? "Unknown error";
        addToast({
          message: `Unable to update user: ${errorMessage}`,
          variant: "error",
        });
      });
  };

  return (
    <Modal
      size="small"
      onClose={() => {
        onClose();
        sendEvent("Close");
      }}
      isPinned
      data-testid="edit-user-modal"
    >
      {open && (
        <>
          <Modal.Header
            title={user.name}
            subtitle={user.email}
            onClose={() => {
              onClose();
              sendEvent("Close");
            }}
          />
          <form onSubmit={handleSubmit(updateUser)}>
            <Modal.Body data-testid="edit-user-modal" variant="light">
              <Modal.Section variant="fill">
                <TextInput
                  inputName="Name Input"
                  error={!!formState.errors.name}
                  errorMessage={formState.errors.name?.message}
                  id="user-name-input"
                  data-testid="user-name-input"
                  {...register("name", {
                    validate: (val) =>
                      val.trim() !== "" || "Please enter a name",
                  })}
                  label="Name"
                  data-autofocus
                />
              </Modal.Section>
              <Modal.Section variant="fill">
                <Controller
                  name="role"
                  control={control}
                  render={({ field, fieldState }) => (
                    <SelectInput
                      error={!!fieldState.error}
                      errorMessage={fieldState.error?.message}
                      id="user-role-input"
                      data-testid="user-role-input"
                      label="Role"
                      showLabel
                      placeholder="Select"
                      handleSelectedItemChange={(item) => {
                        if (isOnlyAdmin && item?.value !== "Administrator") {
                          setError("role", {
                            message: "You cannot remove the only administrator",
                          });
                          return;
                        } else {
                          clearErrors("role");
                        }
                        sendEvent("Role Select - Change", {
                          role: item?.value,
                        });
                        field.onChange(item?.value);
                      }}
                      items={userRoleOptions.map((x) => ({
                        label: x,
                        value: x,
                      }))}
                      initialSelectedItem={{
                        label: user.role,
                        value: user.role,
                      }}
                    />
                  )}
                />
              </Modal.Section>
            </Modal.Body>
            <Modal.Footer
              variant="dark"
              buttons={[
                {
                  "data-testid": "save-changes-button",
                  label: "Save changes",
                  disabled: !formState.isValid,
                  isLoading: service.isLoading(),
                },
              ]}
            />
          </form>
        </>
      )}
    </Modal>
  );
};

export default EditUserModal;
